import React, { useEffect, useMemo, useRef, useState } from "react";
import { __ } from "services/Translator";

// Brand tokens (mirror of src/styles/play365 felt-green + gold triad).
const GOLD_TOP = "#F8D870";
const GOLD_MID = "#D9A442";
const FELT_DEEP = "#06200e";

// celebration ::= "micro" | "confetti" | "tier" | falsy
// options ::= { celebration, amount, currency, icon, benefits[], cta }
export function showHubToast(title, message, options) {
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("gc-hub-toast", {
        detail: { title, message, ...(options || {}) },
      })
    );
  }
}

function prefersReducedMotion() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function useCountUp(target, active, reduced, duration = 1100) {
  const [value, setValue] = useState(active ? 0 : target);
  useEffect(() => {
    if (!active || typeof target !== "number" || !isFinite(target)) {
      setValue(target);
      return undefined;
    }
    if (reduced) {
      setValue(target);
      return undefined;
    }
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(Math.round(target * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, active, reduced, duration]);
  return value;
}

// Keyframes/scoped rules are injected with the overlay so this component stays
// self-contained (no dependency on a SCSS partial that may not ship the anims).
const CELEBRATION_CSS = `
.gc-rc { position: fixed; inset: 0; z-index: 2100; display: flex;
  align-items: center; justify-content: center; pointer-events: none;
  font-family: var(--f-body, inherit); }
.gc-rc__backdrop { position: absolute; inset: 0;
  background: radial-gradient(circle at 50% 40%, rgba(20,48,32,.72), ${FELT_DEEP} 80%);
  animation: gcRcFade .4s ease both; }
.gc-rc--tier { pointer-events: auto; }
.gc-rc__piece { position: absolute; top: 0; will-change: transform, opacity; }
.gc-rc__burst { position: absolute; will-change: transform, opacity;
  font-size: 20px; line-height: 1; animation: gcRcBurst .6s ease-out forwards; }
.gc-rc__confetti { font-size: 16px; opacity: 0;
  animation: gcRcFall linear forwards; }
.gc-rc__card { position: relative; text-align: center; padding: 26px 30px;
  border-radius: 18px; max-width: min(92vw, 380px);
  background: linear-gradient(180deg, #114a2b, #0a2814);
  border: 1px solid rgba(248,216,112,.4);
  box-shadow: 0 24px 60px -12px rgba(0,0,0,.75);
  animation: gcRcCardIn .5s cubic-bezier(.5,1.6,.4,1) both; }
.gc-rc__amount { font-family: var(--f-display, inherit); font-size: 40px;
  font-weight: 800; color: ${GOLD_TOP};
  text-shadow: 0 2px 0 rgba(0,0,0,.35); line-height: 1; }
.gc-rc__label { margin-top: 6px; color: var(--t-body, #dfe7df);
  font-size: 13px; letter-spacing: .5px; }
.gc-rc__medalwrap { position: relative; width: 108px; height: 108px;
  margin: 4px auto 14px; display: flex; align-items: center; justify-content: center; }
.gc-rc__ring { position: absolute; inset: 0; border-radius: 50%;
  background: conic-gradient(${GOLD_TOP}, ${GOLD_MID}, ${GOLD_TOP});
  -webkit-mask: radial-gradient(farthest-side, transparent 62%, #000 64%);
  mask: radial-gradient(farthest-side, transparent 62%, #000 64%);
  animation: gcRcRing 1s ease-out both; }
.gc-rc__medal { font-size: 54px; line-height: 1;
  animation: gcRcMedal .6s cubic-bezier(.5,1.7,.4,1) .15s both; }
.gc-rc__benefits { list-style: none; margin: 4px 0 18px; padding: 0;
  text-align: left; display: inline-block; }
.gc-rc__benefits li { color: var(--t-body, #dfe7df); font-size: 13px;
  padding: 4px 0 4px 22px; position: relative;
  animation: gcRcStagger .4s ease both; }
.gc-rc__benefits li::before { content: "🍀"; position: absolute; left: 0; }
.gc-rc__actions { display: flex; gap: 10px; justify-content: center; }
.gc-rc__btn { font: inherit; font-weight: 700; cursor: pointer;
  border-radius: 10px; padding: 10px 18px; border: 1px solid transparent; }
.gc-rc__btn--primary { color: #3a2c00;
  background: linear-gradient(180deg, ${GOLD_TOP}, ${GOLD_MID}); }
.gc-rc__btn--ghost { color: ${GOLD_TOP}; background: transparent;
  border-color: rgba(248,216,112,.5); }
.gc-rc__btn:focus-visible { outline: 2px solid ${GOLD_TOP}; outline-offset: 2px; }
@keyframes gcRcFade { from { opacity: 0; } to { opacity: 1; } }
@keyframes gcRcBurst {
  0% { transform: translate(0,0) scale(.4); opacity: 1; }
  100% { transform: translate(var(--dx), var(--dy)) scale(1); opacity: 0; } }
@keyframes gcRcFall {
  0% { transform: translateY(-8vh) rotate(0deg); opacity: 0; }
  10% { opacity: 1; }
  100% { transform: translateY(108vh) translateX(var(--dx)) rotate(540deg); opacity: 1; } }
@keyframes gcRcCardIn {
  from { transform: translateY(14px) scale(.94); opacity: 0; }
  to { transform: none; opacity: 1; } }
@keyframes gcRcRing { from { transform: rotate(-120deg) scale(.4); opacity: 0; }
  to { transform: none; opacity: 1; } }
@keyframes gcRcMedal { from { transform: scale(.2); opacity: 0; } to { transform: none; opacity: 1; } }
@keyframes gcRcStagger { from { transform: translateY(8px); opacity: 0; } to { transform: none; opacity: 1; } }
@media (prefers-reduced-motion: reduce) {
  .gc-rc__backdrop, .gc-rc__card, .gc-rc__ring, .gc-rc__medal,
  .gc-rc__benefits li { animation: none !important; }
}
`;

function CoinBurst({ reduced }) {
  const pieces = useMemo(() => {
    const count = reduced ? 0 : 11;
    return Array.from({ length: count }, () => {
      const angle = Math.random() * Math.PI * 2;
      const dist = 40 + Math.random() * 60;
      return {
        dx: `${Math.cos(angle) * dist}px`,
        dy: `${Math.sin(angle) * dist - 30}px`,
        delay: `${Math.random() * 80}ms`,
      };
    });
  }, [reduced]);
  return (
    <div className="gc-rc__card" aria-hidden="true">
      <div style={{ position: "relative", height: 48, width: 48 }}>
        {pieces.map((p, i) => (
          <span
            key={i}
            className="gc-rc__burst"
            style={{ left: 14, top: 14, "--dx": p.dx, "--dy": p.dy, animationDelay: p.delay }}
          >
            🍀
          </span>
        ))}
        <span style={{ fontSize: 30 }}>🍀</span>
      </div>
    </div>
  );
}

function Confetti({ reduced }) {
  const pieces = useMemo(() => {
    const count = reduced ? 0 : 42;
    const glyphs = ["🍀", "🪙", "✦"];
    return Array.from({ length: count }, () => ({
      left: `${Math.random() * 100}%`,
      dx: `${(Math.random() - 0.5) * 120}px`,
      dur: `${2.2 + Math.random() * 1.6}s`,
      delay: `${Math.random() * 0.5}s`,
      glyph: glyphs[Math.floor(Math.random() * glyphs.length)],
    }));
  }, [reduced]);
  return (
    <div className="gc-rc__piece" aria-hidden="true" style={{ inset: 0, width: "100%" }}>
      {pieces.map((p, i) => (
        <span
          key={i}
          className="gc-rc__confetti"
          style={{
            position: "absolute",
            left: p.left,
            "--dx": p.dx,
            animationDuration: p.dur,
            animationDelay: p.delay,
          }}
        >
          {p.glyph}
        </span>
      ))}
    </div>
  );
}

function formatAmount(value, currency) {
  const num = typeof value === "number" ? value : Number(value);
  const text = isFinite(num) ? num.toLocaleString() : String(value);
  return currency ? `${text} ${currency}` : text;
}

function RewardCelebration({ detail, onDone }) {
  const { celebration, amount, currency, icon, benefits, cta } = detail;
  const reduced = useMemo(prefersReducedMotion, []);
  const btnRef = useRef(null);
  const hasAmount = amount !== undefined && amount !== null && amount !== "";
  const shown = useCountUp(Number(amount), hasAmount, reduced);

  const isTier = celebration === "tier";

  useEffect(() => {
    if (isTier) {
      if (btnRef.current) btnRef.current.focus();
      // Safety auto-dismiss so a stuck overlay never blocks the hub.
      const t = setTimeout(onDone, reduced ? 6000 : 12000);
      return () => clearTimeout(t);
    }
    const ms = celebration === "confetti" ? (reduced ? 1400 : 2800) : reduced ? 600 : 900;
    const t = setTimeout(onDone, ms);
    return () => clearTimeout(t);
  }, [celebration, isTier, reduced, onDone]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onDone();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onDone]);

  if (celebration === "micro") {
    return (
      <div className="gc-rc" role="status" aria-live="polite" aria-label={detail.title || __("Reward claimed")}>
        <style>{CELEBRATION_CSS}</style>
        <CoinBurst reduced={reduced} />
      </div>
    );
  }

  if (isTier) {
    const list = Array.isArray(benefits) ? benefits : [];
    return (
      <div
        className="gc-rc gc-rc--tier"
        role="dialog"
        aria-modal="true"
        aria-label={detail.title || __("New tier unlocked")}
      >
        <style>{CELEBRATION_CSS}</style>
        <div className="gc-rc__backdrop" aria-hidden="true" />
        <div className="gc-rc__card">
          <div className="gc-rc__medalwrap">
            <span className="gc-rc__ring" aria-hidden="true" />
            <span className="gc-rc__medal" role="img" aria-label={__("Tier medal")}>
              {icon || "🏅"}
            </span>
          </div>
          <div className="gc-rc__amount" style={{ fontSize: 24 }}>
            {detail.title || __("Tier unlocked")}
          </div>
          {detail.message ? <div className="gc-rc__label">{detail.message}</div> : null}
          {list.length ? (
            <ul className="gc-rc__benefits">
              {list.map((b, i) => (
                <li key={i} style={{ animationDelay: `${0.15 + i * 0.1}s` }}>
                  {b}
                </li>
              ))}
            </ul>
          ) : (
            <div style={{ height: 12 }} />
          )}
          <div className="gc-rc__actions">
            <button
              ref={btnRef}
              type="button"
              className="gc-rc__btn gc-rc__btn--primary"
              onClick={onDone}
            >
              {cta || __("Continue")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // "confetti"
  return (
    <div className="gc-rc" role="status" aria-live="polite" aria-label={detail.title || __("Reward claimed")}>
      <style>{CELEBRATION_CSS}</style>
      <div className="gc-rc__backdrop" aria-hidden="true" />
      <Confetti reduced={reduced} />
      <div className="gc-rc__card">
        {hasAmount && isFinite(Number(amount)) ? (
          <div className="gc-rc__amount">{formatAmount(shown, currency)}</div>
        ) : (
          <div className="gc-rc__medal" role="img" aria-label={__("Reward")}>
            {icon || "🍀"}
          </div>
        )}
        <div className="gc-rc__label">{detail.title || __("Reward claimed")}</div>
        {detail.message ? (
          <div className="gc-rc__label" style={{ opacity: 0.8 }}>
            {detail.message}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function HubToast() {
  const [toast, setToast] = useState(null);
  const [celebration, setCelebration] = useState(null);
  const timerRef = useRef(null);
  const keyRef = useRef(0);

  useEffect(() => {
    const onToast = (e) => {
      const detail = e.detail || {};
      setToast(detail);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setToast(null), 3500);
      if (detail.celebration) {
        keyRef.current += 1;
        setCelebration({ ...detail, _k: keyRef.current });
      }
    };
    window.addEventListener("gc-hub-toast", onToast);
    return () => {
      window.removeEventListener("gc-hub-toast", onToast);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <>
      {celebration ? (
        <RewardCelebration
          key={celebration._k}
          detail={celebration}
          onDone={() => setCelebration(null)}
        />
      ) : null}
      {toast ? (
        <div className="gc-hub-toast" role="status">
          <div className="gc-hub-toast__icon">✓</div>
          <div>
            <div className="gc-hub-toast__title">{toast.title}</div>
            <div className="gc-hub-toast__msg">{toast.message}</div>
          </div>
        </div>
      ) : null}
    </>
  );
}
